import { useEffect, useState } from 'react';
import {
  Alert, Badge, Box, Button, Chip, CircularProgress, Collapse, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Tooltip, Typography, alpha, useMediaQuery, useTheme,
} from '@mui/material';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import dayjs from 'dayjs';
import { IHistoricoEmissao, NfeService } from '../../../shared/services/api/Financeiro/fiscal';

// Histórico de tentativas de emissão de uma NF-e/NFC-e: cada vez que alguém
// tentou emitir a nota fica uma linha (ver providers/historicoemissao no
// backend) — com sucesso ou com o erro completo, a etapa em que parou, o
// código da SEFAZ, o detalhe técnico, quem tentou e quando. Serve pra
// entender por que uma nota ficou pendente e o que corrigir.

type TTipo = 'nfe' | 'nfce';

interface IDialogHistoricoEmissaoProps {
  open: boolean;
  tipo: TTipo;
  iddocumento: string;
  titulo: string; // ex.: "NFC-e 62"
  onClose: () => void;
}

export const DialogHistoricoEmissao: React.FC<IDialogHistoricoEmissaoProps> = ({ open, tipo, iddocumento, titulo, onClose }) => {
  const theme = useTheme();
  const celular = useMediaQuery(theme.breakpoints.down('sm'));
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [historico, setHistorico] = useState<IHistoricoEmissao[]>([]);
  const [abertos, setAbertos] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!open || !iddocumento) return;
    setCarregando(true);
    setErro('');
    setAbertos(new Set());
    NfeService.getHistoricoEmissao(tipo, iddocumento).then((r) => {
      setCarregando(false);
      if (r instanceof Error) { setErro(r.message); setHistorico([]); return; }
      setHistorico(r);
    });
  }, [open, tipo, iddocumento]);

  const qtdErros = historico.filter((h) => !h.sucesso).length;
  const alternarDetalhe = (id: string) => setAbertos((atual) => {
    const novo = new Set(atual);
    if (novo.has(id)) novo.delete(id); else novo.add(id);
    return novo;
  });
  const copiar = (h: IHistoricoEmissao) => {
    const texto = [
      `${titulo} — tentativa ${h.tentativa} em ${dayjs(h.datahora).format('DD/MM/YYYY HH:mm:ss')}`,
      `Etapa: ${h.etapa}`,
      ...(h.configuracao || []).map((c) => `${c.rotulo}: ${c.valor}`),
      h.cstat ? `Código SEFAZ: ${h.cstat}` : '',
      `Motivo: ${h.motivo}`,
      h.detalhe ? `Detalhe técnico:\n${h.detalhe}` : '',
    ].filter(Boolean).join('\n');
    navigator.clipboard?.writeText(texto).catch(() => undefined);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" fullScreen={celular}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <HistoryRoundedIcon color="primary" />
        <Box flex={1}>
          <Typography fontWeight={800}>Histórico de emissão — {titulo}</Typography>
          <Typography variant="caption" color="text.secondary">
            Todas as vezes que a nota foi enviada, com o resultado de cada tentativa.
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {carregando && <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>}
        {erro && <Alert severity="error">{erro}</Alert>}
        {!carregando && !erro && historico.length === 0 && (
          <Alert severity="info">
            Nenhuma tentativa registrada para esta nota. O histórico passou a ser gravado a partir da atualização do sistema —
            tentativas anteriores a ela não aparecem aqui.
          </Alert>
        )}
        {!carregando && historico.length > 0 && (
          <>
            <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
              <Chip label={`${historico.length} tentativa(s)`} size="small" />
              {qtdErros > 0 && <Chip color="error" size="small" label={`${qtdErros} com erro`} />}
              {historico.some((h) => h.sucesso) && <Chip color="success" size="small" label="Autorizada" />}
            </Box>
            <Box display="flex" flexDirection="column" gap={1.25}>
              {historico.map((h) => {
                const cor = h.sucesso ? theme.palette.success.main : theme.palette.error.main;
                const aberto = abertos.has(h.idhistorico);
                return (
                  <Box key={h.idhistorico} sx={{ p: 1.5, borderRadius: 2, border: `1px solid ${alpha(cor, 0.35)}`, bgcolor: alpha(cor, 0.05) }}>
                    <Box display="flex" alignItems="flex-start" gap={1.25}>
                      {h.sucesso ? <CheckCircleRoundedIcon color="success" sx={{ mt: 0.25 }} /> : <ErrorRoundedIcon color="error" sx={{ mt: 0.25 }} />}
                      <Box flex={1} minWidth={0}>
                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                          <Typography fontWeight={800}>Tentativa {h.tentativa}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {dayjs(h.datahora).format('DD/MM/YYYY [às] HH:mm:ss')}{h.usuario ? ` · ${h.usuario}` : ''}
                          </Typography>
                          {h.cstat && <Chip size="small" variant="outlined" label={`SEFAZ ${h.cstat}`} sx={{ height: 20, fontSize: '0.7rem' }} />}
                        </Box>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          <Box component="span" sx={{ fontWeight: 700 }}>{h.sucesso ? 'Resultado: ' : 'Parou em: '}</Box>
                          {h.etapa}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {h.motivo}
                        </Typography>
                        {/* Com o que a nota foi enviada nessa tentativa
                            (forma de pagamento, bandeira, cód. autorização...). */}
                        {!!h.configuracao?.length && (
                          <Box display="flex" gap={0.75} flexWrap="wrap" sx={{ mt: 0.75 }}>
                            {h.configuracao.map((c) => (
                              <Chip
                                key={c.rotulo}
                                size="small"
                                variant="outlined"
                                label={<><Box component="span" sx={{ color: 'text.secondary' }}>{c.rotulo}:</Box> {c.valor}</>}
                                sx={{ height: 22, fontSize: '0.72rem' }}
                              />
                            ))}
                          </Box>
                        )}
                        {h.detalhe && (
                          <>
                            <Button size="small" onClick={() => alternarDetalhe(h.idhistorico)} sx={{ mt: 0.5, px: 0, textTransform: 'none' }}>
                              {aberto ? 'Esconder detalhe técnico' : 'Ver detalhe técnico'}
                            </Button>
                            <Collapse in={aberto}>
                              <Box
                                component="pre"
                                sx={{
                                  m: 0, mt: 0.5, p: 1, borderRadius: 1, fontSize: '0.72rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                                  bgcolor: alpha(theme.palette.text.primary, 0.05), maxHeight: 240, overflow: 'auto',
                                }}
                              >
                                {h.detalhe}
                              </Box>
                            </Collapse>
                          </>
                        )}
                      </Box>
                      {!h.sucesso && (
                        <Tooltip title="Copiar o erro (para mandar ao suporte)">
                          <IconButton size="small" onClick={() => copiar(h)}><ContentCopyRoundedIcon fontSize="small" /></IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
};

// Ícone da listagem: relógio com a quantidade de tentativas com erro
// (vermelho quando a nota tem erro pendente). Tooltip mostra o último motivo.
interface IBotaoHistoricoEmissaoProps {
  qtdErros?: number;
  ultimoMotivo?: string | null;
  ultimaEtapa?: string | null;
  onClick: (evento: React.MouseEvent) => void;
}

export const BotaoHistoricoEmissao: React.FC<IBotaoHistoricoEmissaoProps> = ({ qtdErros = 0, ultimoMotivo, ultimaEtapa, onClick }) => {
  const temErro = !!ultimoMotivo;
  const dica = temErro
    ? `Último erro${ultimaEtapa ? ` (${ultimaEtapa})` : ''}: ${String(ultimoMotivo).slice(0, 180)}${String(ultimoMotivo).length > 180 ? '…' : ''}`
    : qtdErros > 0 ? `${qtdErros} tentativa(s) com erro antes da autorização` : 'Histórico de emissão';
  return (
    <Tooltip title={dica} arrow>
      <IconButton size="small" onClick={(e) => { e.stopPropagation(); onClick(e); }} aria-label="Histórico de emissão">
        <Badge
          badgeContent={qtdErros}
          color={temErro ? 'error' : 'warning'}
          max={99}
          invisible={!qtdErros}
          sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', height: 16, minWidth: 16, px: 0.5 } }}
        >
          <HistoryRoundedIcon fontSize="small" color={temErro ? 'error' : 'action'} />
        </Badge>
      </IconButton>
    </Tooltip>
  );
};
